import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Button,
  Box,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import {
  CreateProductAPI,
  UpdateProductAPI,
} from "../../../../api/product/ProductController";
import { toast } from "react-toastify";
import _JWTDecodeService from "../../../../service/JWTDecodeService";
import { _DecryptService } from "../../../../service/EncryptDecryptService";
import MultiSelectBox from "../../../MutlitSelectBox/MultiSelectBox";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import theme from "../../../../theme";
import { _fetchCategoriesService } from "../../../../service/MasterDataService";

const ProductInformationForm = ({
  handleNext,
  handleBack,
  setProductData,
  productData,
  oldData,
  status,
  steps,
  onRefresh,
}) => {
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const [categories, setCategories] = useState([]);
  const [marketingCategories, setMarketingCategories] = useState([]);
  const [unitCategories, setUnitCategories] = useState([]);
  const [smallestUnitTitle, setSmallestUnitTitle] = useState("");
  const [unitPrices, setUnitPrices] = useState({});
  const [unitQuantities, setUnitQuantities] = useState({});
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [maetrials, setMaterials] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]); // Handle multiple files
  const [imagePreviews, setImagePreviews] = useState([]); // Handle previews for multiple images
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const baseURL = process.env.REACT_APP_RESOURCE_ENDPOINT;
  const formData = new FormData();
  const [removedImages, setRemovedImages] = useState([]); // Store removed image filenames
  const [productDataState, setProductDataState] = useState({
    ProductCode: "",
    MarketingCategory: "",
    Category: "",
    Color: "",
    Size: "",
    Material: "",
    ProductTitle: "",
    ProductDescription: "",
    Price: "",
    Quantity: "",
  });

  useEffect(() => {
    const fetchDropdownData = async () => {
      const timestamp = new Date().getTime();
      const normalizeBusinessKey = (value) =>
        String(value || "")
          .toLowerCase()
          .replace(/\s+/g, "");

      try {
        const categoriesResponse = await fetch(
          process.env.REACT_APP_RESOURCE_ENDPOINT +
            "Category.json?t=" +
            timestamp,
        );
        const categoriesData = await categoriesResponse.json();

        const marketingCategoriesResponse = await fetch(
          process.env.REACT_APP_RESOURCE_ENDPOINT +
            "MarketingCategory.json?t=" +
            timestamp,
        );
        const marketingCategoriesData =
          await marketingCategoriesResponse.json();

        const unitCategoriesResponse = await fetch(
          process.env.REACT_APP_RESOURCE_ENDPOINT +
            "UnitCategory.json?t=" +
            timestamp,
        );
        const unitCategoriesData = await unitCategoriesResponse.json();

        const businessName = process.env.REACT_APP_BUSINESS_NAME;
        const matchedBusinessKey =
          businessName && categoriesData && typeof categoriesData === "object"
            ? Object.keys(categoriesData).find(
                (key) =>
                  normalizeBusinessKey(key) ===
                  normalizeBusinessKey(businessName),
              )
            : null;
        const businessCategories =
          matchedBusinessKey && Array.isArray(categoriesData?.[matchedBusinessKey])
            ? categoriesData[matchedBusinessKey]
            : null;
        setCategories(
          Array.isArray(businessCategories)
            ? Array.from(new Set(businessCategories.map((item) => String(item).trim()).filter(Boolean)))
            : Array.isArray(categoriesData?.Categories)
              ? categoriesData.Categories
              : [],
        );

        let marketingCategoryList = [];
        if (Array.isArray(marketingCategoriesData)) {
          const filtered = businessName
            ? marketingCategoriesData.filter(
                (item) =>
                  item?.businessName === businessName &&
                  item?.isActive === true,
              )
            : marketingCategoriesData.filter((item) => item?.isActive === true);
          marketingCategoryList = filtered
            .map((item) => item?.title)
            .filter(Boolean);
        } else if (Array.isArray(marketingCategoriesData?.Categories)) {
          marketingCategoryList = marketingCategoriesData.Categories;
        }
        const normalizedMarketing = marketingCategoryList
          .map((item) => String(item).trim())
          .filter(Boolean);
        const dedupedMarketing = Array.from(
          new Map(
            normalizedMarketing.map((item) => [item.toLowerCase(), item]),
          ).values(),
        );
        setMarketingCategories(dedupedMarketing);

        let unitCategoryList = [];
        if (Array.isArray(unitCategoriesData)) {
          const filtered = businessName
            ? unitCategoriesData.filter(
                (item) =>
                  item?.businessName === businessName &&
                  item?.isActive === true,
              )
            : unitCategoriesData.filter((item) => item?.isActive === true);
          unitCategoryList = filtered
            .map((item) => item?.title)
            .filter(Boolean);
          const smallestUnit = filtered.find(
            (item) =>
              item?.conversion?.multiplier === 1 &&
              item?.conversion?.toUnit === item?.unitKey,
          );
          setSmallestUnitTitle(smallestUnit?.title || "");
        } else if (Array.isArray(unitCategoriesData?.Categories)) {
          unitCategoryList = unitCategoriesData.Categories;
        }
        setUnitCategories(Array.from(new Set(unitCategoryList)));
        // Color, size, and material dropdowns are hidden in the UI.
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (status === "edit" && oldData) {
      const previews = Array.isArray(oldData.dataLst)
        ? oldData.dataLst
            .map(
              (item) =>
                `${baseURL}Upload/Ecommerce/${item.businessName}/${item.product_Id}/${item.image}`,
            )
            .filter(Boolean)
        : [];
      if (previews.length > 0) {
        setImagePreviews(Array.from(new Set(previews)));
      }
      const parsedUnitPrices = {};
      const parsedUnitQuantities = {};
      if (typeof oldData.price === "string" && oldData.price.includes("-")) {
        oldData.price.split(",").forEach((entry) => {
          const trimmedEntry = entry.trim();
          if (!trimmedEntry) {
            return;
          }
          const [title, priceValue = "", quantityValue = ""] = trimmedEntry
            .split("-")
            .map((part) => part.trim());
          if (title && priceValue) {
            parsedUnitPrices[title] = priceValue;
          }
          if (title && quantityValue) {
            parsedUnitQuantities[title] = quantityValue;
          }
        });
      }
      if (Object.keys(parsedUnitPrices).length > 0) {
        setUnitPrices((prevPrices) => ({
          ...prevPrices,
          ...parsedUnitPrices,
        }));
      }
      if (Object.keys(parsedUnitQuantities).length > 0) {
        setUnitQuantities((prevQuantities) => ({
          ...prevQuantities,
          ...parsedUnitQuantities,
        }));
      } else if (oldData.quantity) {
        const fallbackQuantities = (Array.isArray(unitCategories) ? unitCategories : [])
          .filter(Boolean)
          .reduce((acc, title) => {
            acc[title] = oldData.quantity;
            return acc;
          }, {});
        if (Object.keys(fallbackQuantities).length > 0) {
          setUnitQuantities((prevQuantities) => ({
            ...prevQuantities,
            ...fallbackQuantities,
          }));
        }
      }
      const defaultUnitTitle =
        smallestUnitTitle ||
        ((Array.isArray(unitCategories) ? unitCategories : []).filter(Boolean)[0] || "");
      const derivedSmallestUnitPrice =
        defaultUnitTitle && parsedUnitPrices[defaultUnitTitle]
          ? parsedUnitPrices[defaultUnitTitle]
          : oldData.price || "";
      const derivedSmallestUnitQuantity =
        defaultUnitTitle && parsedUnitQuantities[defaultUnitTitle]
          ? parsedUnitQuantities[defaultUnitTitle]
          : oldData.quantity || "";
      setProductDataState({
        ProductCode: oldData.productCode || "",
        MarketingCategory: oldData.marketingCategory || "",
        Category: oldData.category || "",
        Color: oldData.color || "",
        Size: oldData.size || "",
        ProductTitle: oldData.productTitle || "",
        ProductDescription: oldData.productDescription || "",
        Price: derivedSmallestUnitPrice,
        Quantity: derivedSmallestUnitQuantity,
        Material: oldData.materials || "",
      });
      if (
        defaultUnitTitle &&
        derivedSmallestUnitPrice &&
        !parsedUnitPrices[defaultUnitTitle]
      ) {
        setUnitPrices((prevPrices) => ({
          ...prevPrices,
          [defaultUnitTitle]: derivedSmallestUnitPrice,
        }));
      }
      if (
        defaultUnitTitle &&
        derivedSmallestUnitQuantity &&
        !parsedUnitQuantities[defaultUnitTitle]
      ) {
        setUnitQuantities((prevQuantities) => ({
          ...prevQuantities,
          [defaultUnitTitle]: derivedSmallestUnitQuantity,
        }));
      }
      console.log("OldDataIMageList", oldData.dataLst);
    } else if (productData) {
      setProductData({
        MarketingCategory: productData.MarketingCategory || "",
        Category: productData.Category || "",
        Color: productData.Color || "",
        Size: productData.Size || "",
        ProductTitle: productData.ProductTitle || "",
        ProductDescription: productData.ProductDescription || "",
        Price: productData.Price || "",
        Quantity: productData.Quantity || "",
      });
    }
  }, [
    status,
    oldData,
    productData,
    setProductData,
    smallestUnitTitle,
    unitCategories,
    baseURL,
  ]);

  const generateProductCode = () => {
    const randomChar = String.fromCharCode(65 + Math.floor(Math.random() * 26));

    const randomDigit = Math.floor(Math.random() * 10).toString();

    let additionalChars = Math.random()
      .toString(36)
      .substring(2, 7)
      .toUpperCase();

    additionalChars = additionalChars.replace(/[^A-Z0-9]/g, "");
    additionalChars = additionalChars.slice(0, 4);

    const randomCode = (randomChar + randomDigit + additionalChars).slice(0, 6);
    console.log("Random Code:", randomCode);
    setProductDataState((prevData) => ({
      ...prevData,
      ProductCode: randomCode,
    }));
    // productDataState.ProductCode = randomCode;
    // oldData.productCode=randomCode;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProductDataState((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUnitPriceChange = (unitTitle, value) => {
    setUnitPrices((prevPrices) => ({
      ...prevPrices,
      [unitTitle]: value,
    }));
    const defaultUnitTitle =
      smallestUnitTitle ||
      ((Array.isArray(unitCategories) ? unitCategories : []).filter(Boolean)[0] || "");
    if (unitTitle === defaultUnitTitle) {
      setProductDataState((prevData) => ({
        ...prevData,
        Price: value,
      }));
    }
  };

  const handleUnitQuantityChange = (unitTitle, value) => {
    setUnitQuantities((prevQuantities) => ({
      ...prevQuantities,
      [unitTitle]: value,
    }));
    const defaultUnitTitle =
      smallestUnitTitle ||
      ((Array.isArray(unitCategories) ? unitCategories : []).filter(Boolean)[0] || "");
    if (unitTitle === defaultUnitTitle) {
      setProductDataState((prevData) => ({
        ...prevData,
        Quantity: value,
      }));
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const existingCount = status === "edit" ? oldData?.dataLst?.length || 0 : 0;
    const remainingSlots = 5 - (selectedFiles.length + existingCount);
    const buildFileKey = (file) =>
      `${file.name}-${file.size}-${file.lastModified}`;
    const existingFileKeys = new Set(selectedFiles.map(buildFileKey));
    if (remainingSlots <= 0) {
      alert("You can only upload a maximum of 5 photos.");
      return;
    }

    const typeAllowedFiles = files.filter((file) =>
      ["image/jpeg", "image/png", "image/jpg"].includes(file.type),
    );
    if (typeAllowedFiles.length !== files.length) {
      alert("Only JPEG, PNG, and JPG formats are allowed.");
    }
    const validFiles = typeAllowedFiles.filter(
      (file) => !existingFileKeys.has(buildFileKey(file)),
    );

    const nextFiles = validFiles.slice(0, remainingSlots);
    if (nextFiles.length < validFiles.length) {
      alert("You can only upload a maximum of 5 photos.");
    }

    setSelectedFiles((prevFiles) => [...prevFiles, ...nextFiles]);

    const newPreviews = nextFiles.map((file) => URL.createObjectURL(file));
    setImagePreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);
  };

  const availableUnitTitles = (Array.isArray(unitCategories) ? unitCategories : []).filter(Boolean);
  const primaryUnitTitle = smallestUnitTitle || availableUnitTitles[0] || "";
  const smallestUnitPrice = primaryUnitTitle
    ? unitPrices[primaryUnitTitle]
    : productDataState.Price;
  const smallestUnitQuantity = primaryUnitTitle
    ? unitQuantities[primaryUnitTitle]
    : productDataState.Quantity;
  const normalizeValue = (value) =>
    typeof value === "string" ? value.trim() : value;
  const hasValue = (value) =>
    normalizeValue(value) !== "" && normalizeValue(value) !== undefined;
  const isNextDisabled =
    status === "create"
      ? !hasValue(productDataState.ProductCode) ||
        !hasValue(productDataState.Category) ||
        !hasValue(productDataState.ProductTitle) ||
        !hasValue(productDataState.ProductDescription) ||
        !hasValue(smallestUnitQuantity) ||
        !hasValue(smallestUnitPrice) ||
        selectedFiles.length === 0
      : !hasValue(productDataState.ProductCode) ||
        !hasValue(productDataState.Category) ||
        !hasValue(productDataState.ProductTitle) ||
        !hasValue(productDataState.ProductDescription) ||
        !hasValue(smallestUnitQuantity) ||
        !hasValue(smallestUnitPrice) ||
        imagePreviews.length === 0;

  const handleFormSubmit = async () => {
    setIsLoading(true); // Set loading state to true when the form is submitted

    const requiredErrors = [];
    if (!hasValue(productDataState.ProductCode)) {
      requiredErrors.push("Product Code is required.");
    }
    if (!hasValue(productDataState.Category)) {
      requiredErrors.push("Category is required.");
    }
    if (!hasValue(productDataState.ProductTitle)) {
      requiredErrors.push("Product Title is required.");
    }
    if (!hasValue(productDataState.ProductDescription)) {
      requiredErrors.push("Product Description is required.");
    }
    if (!hasValue(smallestUnitQuantity)) {
      requiredErrors.push(
        primaryUnitTitle
          ? `${primaryUnitTitle} Quantity is required.`
          : "Quantity is required.",
      );
    }
    if (!hasValue(smallestUnitPrice)) {
      requiredErrors.push(
        primaryUnitTitle
          ? `${primaryUnitTitle} Price is required.`
          : "Price is required.",
      );
    }
    const activeUnits = availableUnitTitles;
    activeUnits.forEach((unitTitle) => {
      if (!hasValue(unitPrices[unitTitle])) {
        requiredErrors.push(`${unitTitle} Price is required.`);
      }
      if (!hasValue(unitQuantities[unitTitle])) {
        requiredErrors.push(`${unitTitle} Quantity is required.`);
      }
    });
    if (status === "create" && selectedFiles.length === 0) {
      requiredErrors.push("At least one product image is required.");
    }
    if (status === "edit" && imagePreviews.length === 0) {
      requiredErrors.push("At least one product image is required.");
    }
    if (requiredErrors.length > 0) {
      requiredErrors.forEach((message) => toast.error(message));
      setIsLoading(false);
      return;
    }

    const isValidCode = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{4,6}$/.test(
      productDataState.ProductCode,
    );
    if (!isValidCode) {
      toast.error(
        "Product Code must be 4-6 alphanumeric characters, including at least one letter and one number.",
      );
      setIsLoading(false);
      return;
    }

    const formData = new FormData();

    status == "edit" && formData.append("ProductId", oldData.productId);
    formData.append("ProductCode", productDataState.ProductCode.toUpperCase());
    console.log("uppder case", productDataState.ProductCode.toUpperCase());
    formData.append("MarketingCategory", productDataState.MarketingCategory || "");
    formData.append("Category", productDataState.Category);
    formData.append("Color", productDataState.Color || "");
    formData.append("Size", productDataState.Size || "");
    formData.append("Materials", productDataState.Material || "");
    formData.append("ProductTitle", productDataState.ProductTitle);
    formData.append("ProductDescription", productDataState.ProductDescription);
    const priceEntries = (Array.isArray(unitCategories) ? unitCategories : [])
      .map((unitTitle) => ({
        title: unitTitle,
        price: unitPrices[unitTitle],
        quantity: unitQuantities[unitTitle],
      }))
      .filter(
        (item) =>
          item.price !== "" &&
          item.price !== undefined &&
          item.price !== null &&
          item.quantity !== "" &&
          item.quantity !== undefined &&
          item.quantity !== null,
      );
    if (
      priceEntries.length === 0 &&
      primaryUnitTitle &&
      smallestUnitPrice &&
      smallestUnitQuantity
    ) {
      priceEntries.push({
        title: primaryUnitTitle,
        price: smallestUnitPrice,
        quantity: smallestUnitQuantity,
      });
    }
    const priceValue = priceEntries
      .map((item) => `${item.title}-${item.price}-${item.quantity}`)
      .join(",");
    formData.append("Price", priceValue || "");
    const quantityValue =
      availableUnitTitles.length > 0
        ? availableUnitTitles
            .map((unitTitle) => unitQuantities[unitTitle])
            .filter(
              (qty) => qty !== "" && qty !== undefined && qty !== null,
            )
            .join(",")
        : productDataState.Quantity || "";
    formData.append("Quantity", quantityValue);
    const businessName = _DecryptService(
      _JWTDecodeService(_DecryptService(sessionStorage.getItem("token")))
        .BusinessName,
    );
    formData.append("BusinessName", businessName);
    const UserId = _DecryptService(
      _JWTDecodeService(_DecryptService(sessionStorage.getItem("token")))
        .UserId,
    );
    formData.append("CreateBy", UserId);
    const nowIso = new Date().toISOString();
    formData.append("CreatedDate", nowIso);
    if (status == "edit") {
      formData.append("UpdatedBy", UserId);
      formData.append("UpdatedDate", nowIso);
    }
    selectedFiles.forEach((file, index) => {
      formData.append(`Files`, file);
    });
    // appending remove image list to formData
    console.log("StatusProduct", status);
    if (status == "edit") {
      console.log("RemovedImages", removedImages);
      const uniqueRemovedImages = Array.from(new Set(removedImages));
      uniqueRemovedImages.forEach((fileName, index) => {
        formData.append(`RemoveImages[${index}].FileName`, fileName);
      });
    }
    console.log("material", productDataState.Material);
    if (UserId === "" && businessName === "") {
      // Show an alert box
      const userConfirmed = window.confirm(
        "Session Expired. You will be redirected to the login page.",
      );
      if (userConfirmed) {
        sessionStorage.removeItem("token");
        window.location.replace("/auth?type=logIn");
      }
    } else {
      try {
        if (status == "create") {
          await CreateProductAPI(formData, setIsLoading, handleBack);
        } else {
          await UpdateProductAPI(
            formData,
            setIsLoading,
            handleBack,
            setProductData,
          );
          await onRefresh();
        }

        // handleNext(); // Proceed to the next step after successful submission
      } catch (error) {
        setIsLoading(false); // Reset loading state in case of error
        toast.error("Failed to create product."); // Show error toast if the API fails
      }
    }
  };

  const handleRemoveImage = (index) => {
    const removedFile = imagePreviews[index]; // Get the removed file
    if (removedFile && removedFile.startsWith(baseURL)) {
      const fileName = removedFile.split("/").pop();
      if (fileName) {
        setRemovedImages((prev) => [...prev, fileName]); // Store filename
      }
    }

    setImagePreviews((prevPreviews) =>
      prevPreviews.filter((_, i) => i !== index),
    );
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <Grid
      container
      spacing={4}
      width={"100%"}
      paddingX={15}
      paddingY={10}
      sx={{ display: "flex", flexDirection: "column" }}
    >
      <Typography variant="h5" sx={{ fontWeight: "700" }}>
        {status == "create"
          ? "Product Information"
          : "Edit Product Information"}
      </Typography>

      <Grid
        item
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          alignItems: "flex-end",
        }}
      >
        <FormControl fullWidth>
          <Typography variant="p" sx={{ marginBottom: "5px" }}>
            Product Code
          </Typography>
          <TextField
            name="ProductCode"
            value={productDataState.ProductCode}
            onChange={handleChange}
            placeholder="Enter or Generate Product Code"
            variant="outlined"
            inputProps={{ maxLength: 6, minLength: 4 }}
          />
          <Typography
            sx={{
              alignSelf: "flex-start",
              color: "#2196F3 ",
            }}
          >
            Product Code must be 4-6 characters long and include at least one
            letter and one number
          </Typography>
        </FormControl>
        <Button
          sx={{
            alignSelf: "center",
            backgroundColor: theme.palette.primary.main,
            height: "57px", // Same as TextField height
            minWidth: "auto", // Prevent unnecessarily wide button
          }}
          onClick={generateProductCode}
          variant="contained" // Optional for consistency
        >
          Generate
        </Button>
      </Grid>

      {/* Existing form fields go here */}
      <Grid
        item
        xs={12}
        sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
      >
        <FormControl fullWidth>
          <Typography variant="p">Category</Typography>
          <Select
            displayEmpty
            name="Category"
            value={productDataState.Category}
            onChange={handleChange}
            input={<OutlinedInput />}
            renderValue={(selected) =>
              selected ? selected : <em>Select a category</em>
            }
            MenuProps={MenuProps}
          >
            <MenuItem disabled value="">
              <em>Select a category</em>
            </MenuItem>
            {(Array.isArray(categories) ? categories : [])
              .map((category, index) => (
                <MenuItem key={index} value={category}>
                  {category}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Color/Size dropdowns hidden per requirement. */}
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
      >
        <FormControl fullWidth>
          <Typography variant="p">Marketing Category</Typography>
          <Select
            displayEmpty
            name="MarketingCategory"
            value={productDataState.MarketingCategory || ""}
            onChange={handleChange}
            input={<OutlinedInput />}
            renderValue={(selected) =>
              selected ? selected : <em>Select a marketing category</em>
            }
            MenuProps={MenuProps}
          >
            <MenuItem disabled value="">
              <em>Select a marketing category</em>
            </MenuItem>
            {(Array.isArray(marketingCategories)
              ? marketingCategories
              : []
            ).map((category, index) => (
              <MenuItem key={index} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        {/* Material dropdown hidden per requirement. */}
        <FormControl fullWidth>
          <Typography variant="p">Product Title</Typography>
          <TextField
            name="ProductTitle"
            value={productDataState.ProductTitle}
            onChange={handleChange}
            placeholder="Enter Product Title"
            variant="outlined"
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} sx={{ marginTop: 3 }}>
        <FormControl fullWidth>
          <Typography variant="p">Product Description</Typography>
          <TextField
            name="ProductDescription"
            value={productDataState.ProductDescription}
            onChange={handleChange}
            placeholder="Enter Product Description"
            multiline
            rows={4}
            variant="outlined"
          />
        </FormControl>
      </Grid>

      {availableUnitTitles.length > 0 ? (
        availableUnitTitles.map((unitTitle, index) => (
            <Grid
              item
              xs={12}
              key={unitTitle || index}
              sx={{
                marginTop: 3,
                display: "flex",
                flexDirection: "row",
                gap: "10px",
              }}
            >
              <FormControl fullWidth>
                <Typography variant="p">{unitTitle} Price</Typography>
                <TextField
                  type="number"
                  value={unitPrices[unitTitle] ?? ""}
                  onChange={(event) =>
                    handleUnitPriceChange(unitTitle, event.target.value)
                  }
                  onWheel={(event) => event.target.blur()}
                  placeholder={`Enter ${unitTitle} Price`}
                  variant="outlined"
                />
              </FormControl>
              <FormControl fullWidth>
                <Typography variant="p">{unitTitle} Quantity</Typography>
                <TextField
                  type="number"
                  value={unitQuantities[unitTitle] ?? ""}
                  onChange={(event) =>
                    handleUnitQuantityChange(unitTitle, event.target.value)
                  }
                  onWheel={(event) => event.target.blur()}
                  placeholder={`Enter ${unitTitle} Quantity`}
                  variant="outlined"
                />
              </FormControl>
            </Grid>
          ))
      ) : (
        <>
          <Grid item xs={12} sx={{ marginTop: 3 }}>
            <FormControl fullWidth>
              <Typography variant="p">Price</Typography>
              <TextField
                type="number"
                value={productDataState.Price ?? ""}
                onChange={handleChange}
                name="Price"
                onWheel={(event) => event.target.blur()}
                placeholder="Enter Price"
                variant="outlined"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: 3 }}>
            <FormControl fullWidth>
              <Typography variant="p">Quantity</Typography>
              <TextField
                name="Quantity"
                type="number"
                value={productDataState.Quantity}
                onChange={handleChange}
                onWheel={(event) => event.target.blur()}
                placeholder="Enter Quantity"
                variant="outlined"
              />
            </FormControl>
          </Grid>
        </>
      )}

      <Grid item xs={12} sx={{ marginTop: 3 }}>
        <FormControl fullWidth>
          <Typography variant="p">Upload Product Photos (Max 5)</Typography>
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleFileChange}
            multiple
          />
        </FormControl>
        <Box sx={{ display: "flex", flexWrap: "wrap", marginTop: 2, gap: 2 }}>
          {imagePreviews.map((preview, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                display: "inline-block",
                maxWidth: "100px",
              }}
            >
              <img
                src={preview}
                alt={`Preview ${index}`}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                }}
              />
              {/* Remove Icon */}
              <IconButton
                onClick={() => handleRemoveImage(index)}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: "red",
                  color: "#fff",
                  borderRadius: "50%",
                  padding: "2px",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Grid>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ marginTop: 3, gap: 5 }}
      >
        <Button size="large" variant={"outlined"} onClick={handleBack}>
          Close
        </Button>
        <Button
          size="large"
          variant="contained"
          disabled={isNextDisabled || isLoading}
          onClick={() => handleFormSubmit()}
        >
          {isLoading
            ? status == "create"
              ? "Submitting...."
              : "Updating...."
            : steps > 1
              ? "Next"
              : status == "create"
                ? "Submit"
                : "Update"}
        </Button>
      </Box>
    </Grid>
  );
};

export default ProductInformationForm;
